<template>
  <div
    :class="tableClass"
    :style="{ maxHeight: maxHeight }"
    class="table_with_filter"
    ref="tableWrap"
  >
    <div v-if="loading" class="linear-activity">
      <div class="indeterminate"></div>
    </div>
    <table>
      <thead>
        <tr>
          <th
            @dblclick="unbindEvents"
            :data-orderable="header.index"
            @mousedown="headerKeyDownHandle($event, header.index, index)"
            v-for="(header, index) in headers"
            :key="`header${index}`"
            :style="header.style"
            ref="tableHeader"
            class="header_name_filter_table"
          >
            <slot :name="`header.${header.value}`" :header="header">
              <span
                v-if="header.sortable"
                @click="changeSortHandler(header.value)"
                :class="[
                  'cursor-pointer',
                  'table-wth-children-sortable-th',
                  { active: header.value === options.sortBy },
                ]"
                >{{ header.text }}
                <v-icon small class="table-wth-children-sortable-icon">{{
                  options.sortDirection ? "mdi-arrow-down" : "mdi-arrow-up"
                }}</v-icon></span
              >
              <span v-else>{{ header.text }}</span>
            </slot>
            <div
              v-if="isEnabledResizeTable"
              @mousedown="startResize($event, index)"
              class="table-with-children-head-grip"
            ></div>
          </th>
        </tr>
        <tr v-if="isFilterable">
          <th
            :key="`filter-header-${index}`"
            v-for="(item, index) in headers"
            class="header_filter_table"
          >
            <slot :name="`filter.${item.value}`"></slot>
          </th>
        </tr>
      </thead>
      <tbody>
        <slot name="prepend:body"></slot>
      </tbody>
      <tbody v-for="(item, itemIndex) in items" :key="`bodyItem${itemIndex}`">
        <tr
          :class="[
            'table-wth-children-row table-wth-children-row-parent',
            item.parentClassName,
          ]"
          @click="$emit('click:item', itemIndex)"
        >
          <td
            v-for="header in headers"
            :key="`rowItem-${itemIndex}-${header.value}`"
          >
            <div class="d-flex align-center">
              <slot
                :index="itemIndex"
                :name="`item.${header.value}`"
                :header="header"
                :item="item"
              >
                {{ item[header.value] }}
              </slot>
              <div class="expand-btn-wrap" v-if="header.value === handlerValue">
                <slot
                  name="expandBtn"
                  :item="item"
                  :header="header"
                  :handlerValue="handlerValue"
                  :openHandler="showChildHandler"
                >
                </slot>
              </div>
            </div>
          </td>
        </tr>
        <tr
          :class="[
            'table-wth-children-row-item',
            item.childClassName,
            child.className,
          ]"
          v-if="(item[childKey] && item.show) || alwaysOpen"
          v-for="(child, index) in item[childKey]"
          :key="`child-${item.id}-${index}`"
        >
          <td
            :key="`cell-${itemIndex}-${index}-${header.value}`"
            v-for="header in headers"
          >
            <slot
              :itemIndex="itemIndex"
              :index="index + 1"
              :name="`child.${header.value}`"
              :header="header"
              :item="item"
              :child="child"
            >
              {{ child[header.value] }}
            </slot>
          </td>
        </tr>
        <slot name="append:child" :item="item" v-if="item.show"></slot>
        <tr v-if="item[childKey] && item.show && item[childKey].length === 0">
          <slot name="no-data" :length="headers.length">
            <td
              :colspan="headers.length"
              class="text-center"
              style="height: 300px"
            >
              {{ $t("noDataAvailable") }}
            </td>
          </slot>
        </tr>
      </tbody>
      <tbody v-if="items.length === 0">
        <tr>
          <slot name="no-data" :length="headers.length">
            <td
              style="height: 300px"
              :colspan="headers.length"
              class="text-center"
            >
              {{ $t("noDataAvailable") }}
            </td>
          </slot>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { clearSelection } from "@/utils/pureFunctions";

let clickedSameHeader = false;

export default {
  props: {
    isFilterable: {
      type: Boolean,
      default: true,
    },
    isEnabledResizeTable: {
      type: Boolean,
      default: false,
    },
    isEnabledOrderableTable: {
      type: Boolean,
      default: false,
    },
    fixTableColspan: {
      default: 10 * 10,
    },
    open: {
      type: Array,
      default: () => [],
    },
    itemKey: {
      type: String,
      default: "id",
    },
    options: {
      type: Object,
      default: () => {
        return {
          sortBy: null,
          sortDirection: false,
        };
      },
    },
    alwaysOpen: {
      type: Boolean,
      default: false,
    },
    fixedHeader: {
      type: Boolean,
      default: false,
    },
    childKey: {
      type: String,
      default: "children",
    },
    headers: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    height: {
      type: Number,
    },
    openPromise: {},
    handlerValue: {
      type: String,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    page: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      selectedCurrentHeader: null,
    };
  },
  watch: {
    page() {
      const { tableWrap } = this.$refs;
      tableWrap.scrollTop = 0;
    },
  },
  methods: {
    getMaxSize(size) {
      if (size < 600) return 700;
      return size;
    },
    headerMouseMoveHandle({ clientX, clientY }) {
      const { el } = this.selectedCurrentHeader;
      el.style.left = clientX + "px";
      el.style.top = clientY + "px";
    },
    headerMouseUpHandle(e) {
      let th =
        e.target.tagName === "TH" && e.target.dataset.orderable
          ? e.target
          : e.target.closest("th[data-orderable]");
      if (th) {
        const index = +th.dataset.orderable;
        if (this.selectedCurrentHeader.index !== index) {
          this.$emit("change-header-order", {
            oldIndex: this.selectedCurrentHeader.index,
            currentIndex: index,
          });
        }
      }
      this.unbindEvents();
    },
    unbindEvents() {
      document.removeEventListener("mousemove", this.headerMouseMoveHandle);
      document.removeEventListener("mouseup", this.headerMouseUpHandle);
      if (this.selectedCurrentHeader && this.selectedCurrentHeader.el) {
        this.selectedCurrentHeader.el.remove();
      }
      document.body.style.userSelect = "initial";
      this.selectedCurrentHeader = null;
    },
    headerKeyDownHandle({ clientX, clientY }, headerIndex, itemIndex) {
      if (this.isEnabledOrderableTable) {
        const { tableHeader } = this.$refs;
        const table = document.createElement("table");
        table.classList.add("ghost-table");
        const tableHead = document.createElement("thead");
        const th = tableHeader[itemIndex].cloneNode(true);
        th.style.position = "relative";
        tableHead.append(th);
        table.append(tableHead);
        table.style.marginTop = "10px";
        table.style.position = "absolute";
        table.style.zIndex = "9";
        table.style.left = clientX + "px";
        table.style.top = clientY + "px";
        document.body.append(table);
        document.addEventListener("mousemove", this.headerMouseMoveHandle);
        document.addEventListener("mouseup", this.headerMouseUpHandle);
        document.body.style.userSelect = "none";
        this.selectedCurrentHeader = {
          index: headerIndex,
          isStartedMoving: false,
          el: table,
          startX: tableHeader[itemIndex].offsetWidth / 2,
        };
      }
    },
    startResize(e, index) {
      const currentHeader = this.$refs.tableHeader[index];
      const startOffset = currentHeader.offsetWidth - e.pageX;
      function mouseMove(e) {
        currentHeader.style.minWidth = startOffset + e.pageX + "px";
      }
      if (this.isEnabledResizeTable) {
        e.stopPropagation();
        clearSelection();
        currentHeader.parentElement.style.cursor = "col-resize";
        const mouseUp = (e) => {
          document.removeEventListener("mousemove", mouseMove);
          currentHeader.parentElement.style.cursor = "initial";
          document.removeEventListener("mouseup", mouseUp);
          this.$emit("table-header-resized", {
            index,
            header: this.headers[index],
            key: this.headers[index].value,
            width: startOffset + e.pageX + "px",
          });
        };
        document.addEventListener("mousemove", mouseMove);
        document.addEventListener("mouseup", mouseUp);
      }
    },
    async showChildHandler(parent, promise) {
      if (!parent.show && promise) {
        parent.loading = true;
        try {
          await promise(parent);
          parent.show = !parent.show;
          if (parent[this.itemKey]) {
            this.open.push(parent[this.itemKey]);
          }
        } catch (e) {
          console.error(e);
          this.$emit("onException", e);
        } finally {
          parent.loading = false;
        }
      } else {
        parent.show = !parent.show;
        const open = this.open.filter((item) => {
          return parent[this.itemKey] !== item;
        });
        this.$emit("update:open", open);
      }
    },
    changeSortHandler(value) {
      if (this.options.sortBy === value) {
        if (!clickedSameHeader) {
          this.options.sortDirection = !this.options.sortDirection;
        } else {
          this.options.sortBy = null;
        }
        clickedSameHeader = true;
      } else {
        this.options.sortBy = value;
        clickedSameHeader = false;
      }

      this.$emit("update:sort", value);
    },
  },
  computed: {
    maxHeight() {
      let {
        breakpoint: { height },
      } = this.$vuetify;
      return this.height
        ? `${this.getMaxSize(this.height)}px`
        : this.fixedHeader
        ? `${this.getMaxSize(height) - 282}px`
        : "auto";
    },
    tableClass() {
      return [
        "table-wth-children",
        "table_cover",
        {
          "fixed-header": this.fixedHeader,
        },
      ];
    },
  },
};
</script>

<style scoped lang="scss">
.ghost-table {
  th {
    background-color: #ddf2fc;
    border-radius: 5px;
    color: #01456a;
    padding: 5px 10px;
    font-weight: 400;
    box-shadow: 10px 10px 10px rgba(255, 255, 255, 0.5);
  }
}
.linear-activity {
  overflow: hidden;
  width: 50%;
  height: 4px;
  background-color: #b3e5fc;
  margin: 20px auto;
}
.indeterminate {
  position: relative;
  width: 100%;
  height: 100%;
}

.indeterminate:before {
  content: "";
  position: absolute;
  height: 100%;
  background-color: #03a9f4;
  animation: indeterminate_first 1.5s infinite ease-out;
}

.indeterminate:after {
  content: "";
  position: absolute;
  height: 100%;
  background-color: #4fc3f7;
  animation: indeterminate_second 1.5s infinite ease-in;
}

@keyframes indeterminate_first {
  0% {
    left: -100%;
    width: 100%;
  }
  100% {
    left: 100%;
    width: 10%;
  }
}

@keyframes indeterminate_second {
  0% {
    left: -150%;
    width: 100%;
  }
  100% {
    left: 100%;
    width: 10%;
  }
}
</style>
